import {ReportRow} from '../../types'

import copy from './copy.json'
import {
  compareByName,
  daysSourceLabel,
  formatDaysSinceLastTag,
  formatTrained,
} from './formatters'

/*
  CSV delivery for the Tagged Photo Report (AC-16..AC-19).

  The file is built from the *same* row objects the table renders, through the
  *same* formatters (D-J). There is deliberately no second query and no second
  formatting path: "exactly the report the user is looking at" is then true by
  construction rather than because two code paths happen to agree.

  Nothing here makes a network call, and nothing here adds a dependency (D-K).
*/

/** Excel and most spreadsheets are happiest with CRLF record separators. */
const RECORD_SEPARATOR = '\r\n'

const NEEDS_QUOTING = /[",\r\n]/

/**
 * RFC-4180 escaping (AC-18): quote a value that holds a comma, a double quote
 * or a line break, and double any inner quote. A plain value is left plain —
 * quoting everything would be valid CSV but makes the file unreadable by eye.
 */
export const escapeCsvValue = (value: string): string =>
  NEEDS_QUOTING.test(value) ? `"${value.replace(/"/g, '""')}"` : value

const toRecord = (fields: string[]): string =>
  fields.map(escapeCsvValue).join(',')

const pad = (value: number): string => String(value).padStart(2, '0')

/**
 * `Tagged-Photo-Report-<yyyy-MM-dd>-UTC.csv` — the exact shape the legacy
 * report used, so an existing filing habit still works (AC-17).
 *
 * The date is read in UTC, matching the `-UTC` the name itself claims: a
 * late-evening run in a behind-UTC zone must not name the file yesterday.
 * `now` is injected so the name is testable without freezing the clock.
 */
export const buildCsvFilename = (now: Date = new Date()): string => {
  const yyyy = now.getUTCFullYear()
  const mm = pad(now.getUTCMonth() + 1)
  const dd = pad(now.getUTCDate())

  return `Tagged-Photo-Report-${yyyy}-${mm}-${dd}-UTC.csv`
}

interface BuildReportCsvParams {
  rows: ReportRow[]
  /** AC-8 — the bunk column exists in the file exactly when it exists on paper. */
  showBunkColumn: boolean
  /** The camp's own term for a bunk, title-cased, as the printed heading uses. */
  bunkTerm: string
  /** Injected so the day counts are testable without freezing the clock. */
  now?: Date
}

/**
 * Serialize the rows the table renders (AC-16).
 *
 * Ordering is applied here with the same `compareByName` the printed table
 * uses, so the file cannot come out in a different order than the page even if
 * a caller hands over an unordered set.
 */
export const buildReportCsv = ({
  rows,
  showBunkColumn,
  bunkTerm,
  now,
}: BuildReportCsvParams): string => {
  const {columns} = copy.report

  const header = [
    columns.lastName,
    columns.firstName,
    columns.personId,
    columns.sessions,
    columns.divisions,
    ...(showBunkColumn ? [bunkTerm] : []),
    columns.trained,
    columns.taggedPhotoCount,
    columns.daysSinceLastTag,
    // AC-27 — the source travels in its own column so the days column stays a
    // bare number a spreadsheet sorts numerically.
    columns.daysSource,
  ]

  const records = [...rows].sort(compareByName).map((row) => {
    // AC-25 / AC-27 — one call yields both cells, so the exported figure and the
    // exported source cannot disagree with each other or with the printed page.
    const {days, source} = formatDaysSinceLastTag(
      row.taggedPhotoCount,
      row.lastTaggedUTC,
      row.lastTaggedPhotoCreatedUTC,
      now,
    )

    return toRecord([
      row.lastName,
      row.firstName,
      String(row.personId),
      row.sessions,
      row.divisions,
      ...(showBunkColumn ? [row.bunk] : []),
      formatTrained(row.trained),
      String(row.taggedPhotoCount),
      // Blank for a never-tagged camper, exactly as the printed cell is (E-8).
      // A bare number otherwise — never `9 (photo)`, which would make the
      // column text and break the sort the export exists for (AC-27, D-O).
      days,
      daysSourceLabel(source),
    ])
  })

  return [toRecord(header), ...records].join(RECORD_SEPARATOR)
}

/**
 * The download seam (D-K).
 *
 * A Blob and an object URL rather than a data URI, because a data URI carries
 * the whole report inside a URL and hits length limits in some browsers on a
 * large camp. Named and exported rather than inlined at the call site because
 * jsdom implements neither `URL.createObjectURL` nor anchor-driven downloads,
 * so the view's test has to be able to replace it (R-4).
 */
export const downloadCsv = (filename: string, content: string): void => {
  // A BOM so Excel opens the file as UTF-8 rather than the local codepage,
  // which would mangle an accented camper name.
  const blob = new Blob([`\ufeff${content}`], {
    type: 'text/csv;charset=utf-8;',
  })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')

  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)
}
